import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { advisorAdminApi, advisorAdminKeys } from '../api/api';

const preRegTeamKeys = {
  all: [...advisorAdminKeys.all, 'preRegisteredTeamRelationships'] as const,
  list: () => [...preRegTeamKeys.all, 'list'] as const,
};

export function usePreRegisteredTeamRelationshipsList() {
  return useQuery({
    queryKey: preRegTeamKeys.list(),
    queryFn: () => advisorAdminApi.fetchPreRegisteredTeamRelationships(),
  });
}

export function useAddPreRegisteredTeamRelationship() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: { supervisorEmail: string; subordinateEmail: string; accessMode: ClientAccessMode }) =>
      advisorAdminApi.addPreRegisteredTeamRelationship(data),
    onSuccess: () => {
      toast.success('Team relationship pre-registered successfully');
      queryClient.invalidateQueries({ queryKey: preRegTeamKeys.list() });
    },
  });

  return {
    addPreRegisteredTeamRelationship: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useAddPreRegisteredTeamRelationshipsBulk() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: { relationships: Array<{ supervisorEmail: string; subordinateEmail: string; accessMode: ClientAccessMode }> }) =>
      advisorAdminApi.addPreRegisteredTeamRelationshipsBulk(data),
    onSuccess: (data) => {
      toast.success(`${data.inserted} relationship(s) pre-registered, ${data.skipped} skipped (duplicates)`);
      queryClient.invalidateQueries({ queryKey: preRegTeamKeys.list() });
    },
  });

  return {
    addBulk: mutation.mutate,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useSearchPreRegisteredTeamRelationship() {
  const mutation = useMutation({
    mutationFn: (email: string) =>
      advisorAdminApi.searchPreRegisteredTeamRelationship({ email }),
  });

  return {
    search: mutation.mutate,
    data: mutation.data ?? null,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}

export function useDeletePreRegisteredTeamRelationship() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (id: string) =>
      advisorAdminApi.deletePreRegisteredTeamRelationship(id),
    onSuccess: () => {
      toast.success('Pre-registered team relationship removed');
      queryClient.invalidateQueries({ queryKey: preRegTeamKeys.list() });
    },
  });

  return {
    deletePreRegisteredTeamRelationship: mutation.mutate,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}
